'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import SectionHeader from '@/components/SectionHeader';
import { Star, Tv, Loader2, CheckCircle2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

interface Series {
  id: string;
  title: string;
  slug: string;
  year: number;
  imdb_rating: number;
  genre: string;
  poster_url: string;
  season_count: number;
}

interface SeriesWithCompletion extends Series {
  isCompleted: boolean;
}

export default function LatestSeriesSection() {
  const [latestSeries, setLatestSeries] = useState<SeriesWithCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchSeries = useCallback(async () => {
    const { data, error } = await supabase
      .from('series')
      .select('id, title, slug, year, imdb_rating, genre, poster_url, season_count')
      .order('created_at', { ascending: false })
      .limit(6);
    if (!error && data) {
      // Fetch completion status for each series
      const seriesIds = data.map((s) => s.id);
      const completionMap: Record<string, boolean> = {};

      if (seriesIds.length > 0) {
        const { data: episodesData } = await supabase
          .from('episodes')
          .select('series_id, is_completed')
          .in('series_id', seriesIds);

        if (episodesData) {
          seriesIds.forEach((sid) => {
            const eps = episodesData.filter((e) => e.series_id === sid);
            completionMap[sid] = eps.length > 0 && eps.every((e) => e.is_completed === true);
          });
        }
      }

      setLatestSeries(
        data.map((s) => ({ ...s, isCompleted: completionMap[s.id] ?? false }))
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  useRealtimeContent({
    tables: ['series'],
    onAnyChange: () => fetchSeries(),
  });

  if (loading) {
    return (
      <section>
        <SectionHeader
          title="Latest Series"
          subtitle="New seasons and complete collections"
          viewAllHref="/series"
          accentColor="purple"
        />
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-accent" />
        </div>
      </section>
    );
  }

  if (latestSeries.length === 0) {
    return (
      <section>
        <SectionHeader
          title="Latest Series"
          subtitle="New seasons and complete collections"
          viewAllHref="/series"
          accentColor="purple"
        />
        <div className="glass-card border border-border rounded-2xl p-10 text-center">
          <Tv size={32} className="text-accent mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground font-body">No series added yet. Check back soon!</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SectionHeader
        title="Latest Series"
        subtitle="New seasons and complete collections"
        viewAllHref="/series"
        accentColor="purple"
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
        {latestSeries.map((series) => (
          <Link key={`latest-series-${series.id}`} href={`/series/${series.slug}`} className="block group">
            <div className="glass-card glass-card-hover rounded-xl overflow-hidden">
              <div className="relative aspect-[2/3] overflow-hidden">
                <AppImage
                  src={series.poster_url || '/assets/images/no_image.png'}
                  alt={`${series.title} poster — ${series.genre} series`}
                  fill
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                  <Star size={11} className="rating-star fill-yellow-400" />
                  <span className="text-xs font-bold font-mono-data">{series.imdb_rating?.toFixed(1) ?? '—'}</span>
                </div>
                {series.season_count > 0 && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                    <Tv size={10} className="text-accent" />
                    <span className="text-[10px] font-bold text-accent">{series.season_count}S</span>
                  </div>
                )}
                {/* Completed overlay badge */}
                {series.isCompleted && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[1px]">
                    <div className="flex flex-col items-center gap-1.5 bg-green-500/90 rounded-xl px-3 py-2 shadow-lg">
                      <CheckCircle2 size={20} className="text-white" />
                      <span className="text-[11px] font-extrabold text-white tracking-wider uppercase">Completed</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-accent transition-colors duration-200">
                  {series.title}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-muted-foreground font-mono-data">{series.year}</span>
                  <span className="genre-badge" style={{ borderColor: 'rgba(179,71,255,0.25)', color: 'var(--neon-purple)', background: 'rgba(179,71,255,0.1)' }}>{series.genre}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}